/**
 * 기능 플래그 관리
 */

export interface FeatureFlags {
  adminSecurity: boolean
  usePresignedUpload: boolean
  dryRun: boolean
  samlAuth: boolean
  externalImport: boolean
  importV2: boolean
  autofillIcon: boolean
  totpAuthenticator: boolean
  totpGoogleImport: boolean
  totpExport: boolean
  memoTemplate: boolean
  weeklyReport: boolean
}

// dev 환경(korea-dev/japan-dev/us-dev)에서만 켜는 플래그 판별. staging/prod/production 은 OFF.
// conf.js(GitInfo 매크로 의존)를 import하지 않기 위해 REACT_APP_TARGET을 직접 비교(feature-flags는 의존성 없이 유지).
const DEV_TARGETS = ['korea-dev', 'japan-dev', 'us-dev']
// export: services/totp/timeSync의 QA 디버그 훅 게이팅에서 재사용 (PR6)
export const isDevBuild = DEV_TARGETS.includes(process.env.REACT_APP_TARGET || '')

// prod 빌드에서만 끄는 판별 — build:*-production 3리전 공통 REACT_APP_TARGET=production (package.json 실측).
// 미설정은 prod 로 간주하지 않음: 순수 `yarn start`(로컬 dev, TARGET 미지정)까지 꺼지는 부작용 방지.
// jest 는 setupTests.js 가 TARGET 을 'japan-staging' 으로 강제하므로 테스트 환경에선 항상 false.
export const isProdBuild = (process.env.REACT_APP_TARGET || '') === 'production'

/**
 * 현재 활성화된 기능 플래그
 */
export const featureFlags: FeatureFlags = {
  adminSecurity: true,
  usePresignedUpload: true,
  dryRun: false,
  samlAuth: true,
  externalImport: true,
  importV2: true,
  autofillIcon: true,
  // TOTP 인증기: dev 환경에서만 노출(QA용). prod 출시 시 조건 확장. (설계 §확정결정: prod는 전역 동시)
  totpAuthenticator: isDevBuild,
  // Google Authenticator 일괄 import(#1514): totpAuthenticator 의 하위 flag —
  // 1차 TOTP 배포에서 import 만 따로 늦출 수 있게 분리(소비처는 두 flag AND).
  totpGoogleImport: isDevBuild,
  // 레코드별 QR 내보내기(#1535): 동일 취지의 하위 flag(소비처는 두 flag AND).
  totpExport: isDevBuild,
  // 메모 템플릿(rich_note 생성/편집/상세): 전 환경 노출(ON).
  // 노출 지점 4곳이 이 flag 를 AND 로 게이팅한다 —
  // ① 생성 드롭다운 항목(layouts/Main) ② 좌측 메뉴 탭(apptype-config)
  // ③ /records/memo 라우트 페이지(직접 URL 진입 방어) ④ 목록→메모 상세 모달 분기(SearchTablePage.openItem)
  memoTemplate: true,
  // 주간 이용현황 리포트 설정 탭(#1625): prod 빌드에서만 제외(dev/staging/qc 노출) — prod 출시 시 true 전환.
  // 백엔드 발송은 별도 env flag(WEEKLY_REPORT_MAIL_ENABLE, prod 미등록=OFF)라 이중 안전.
  // 노출 지점 2곳(pages/admin/activity/index.js): 탭 items + activeKey 직접 진입 방어
  weeklyReport: !isProdBuild,
}

// data_type 목록에서 기능 플래그로 숨겨야 하는 타입 판별 (드롭다운/템플릿 빌더 공용)
export function isFeatureHiddenType(typeName: string): boolean {
  if (typeName === 'totp' && !featureFlags.totpAuthenticator) return true
  // rich_note(서식 있는 메모)는 메모 레코드 본문 전용 타입 — memoTemplate flag OFF 빌드(prod)에서는
  // 리소스 커스텀 필드/템플릿 빌더 타입 목록에 노출 금지 (data_type.json 은 전 환경 공통이라 FE 게이팅)
  if (typeName === 'rich_note' && !featureFlags.memoTemplate) return true
  return false
}
